import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePermisos } from "../../context/PermisosContext";
import api from "../../services/Api";
import { ESPLoader, Transport } from "esptool-js";

const FIRMWARES = {
  sensores: { name: "Sensores", description: "Lecturas físicas de sensores compatibles. Sin datos simulados; los fallos de lectura no generan mediciones." },
  efectores: { name: "Efectores", description: "Control seguro de relés, bombas, aireadores y otras salidas." },
  mixto: { name: "Mixto", description: "Sensores físicos y efectores en un solo ESP32. Sin simulación; configura y calibra cada módulo antes de usarlo.", recommended: true },
};

const manifestForType = (type) => {
  const pub = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  const folder = type === "efectores" ? "Efectores" : type === "mixto" ? "Mixto" : "Sensores";
  return `${pub}/Firmware/${folder}/manifest.json`;
};

const loadEspWebTools = () => new Promise((resolve, reject) => {
  if (customElements.get("esp-web-install-button")) return resolve();
  const existing = document.querySelector("script[data-esp-web-tools]");
  if (existing) {
    existing.addEventListener("load", resolve, { once: true });
    existing.addEventListener("error", reject, { once: true });
    return;
  }
  const script = document.createElement("script");
  script.type = "module";
  script.dataset.espWebTools = "1";
  script.src = "https://unpkg.com/esp-web-tools@10/dist/web/install-button.js?module";
  script.onload = resolve;
  script.onerror = () => reject(new Error("No se pudo cargar ESP Web Tools."));
  document.head.appendChild(script);
});

const ESP_TOOLS_TRANSLATIONS = new Map([
  ["Installing", "Instalando"],
  ["Preparing installation", "Preparando la instalación"],
  ["Erase device", "Borrar dispositivo"],
  ["Do you want to erase the device before installing", "¿Quieres borrar el dispositivo antes de instalar"],
  ["All data on the device will be lost.", "Se borrarán todos los datos del dispositivo."],
  ["Confirm Installation", "Confirmar instalación"],
  ["Do you want to install", "¿Quieres instalar"],
  ["All data on the device will be erased.", "Se borrarán todos los datos del dispositivo."],
  ["BACK", "ATRÁS"],
  ["Back", "ATRÁS"],
  ["NEXT", "SIGUIENTE"],
  ["INSTALL", "INSTALAR"],
  ["Installation failed", "La instalación falló"],
  ["Failed to initialize. Try resetting your device or holding the BOOT button while clicking INSTALL.", "No se pudo iniciar el ESP32. Reinícialo o mantén presionado BOOT mientras haces clic en INSTALAR."],
  ["Try again", "Intentar de nuevo"],
  ["Close", "Cerrar"],
  ["Connecting", "Conectando"],
  ["Erasing device", "Borrando el dispositivo"],
  ["Installing firmware", "Instalando el firmware"],
  ["Installation complete", "Instalación completada"],
]);

const translateEspToolsNode = (root) => {
  if (!root) return;
  const visit = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const original = node.nodeValue;
      const trimmed = original?.trim();
      if (!trimmed) return;
      let translated = trimmed;
      [...ESP_TOOLS_TRANSLATIONS.entries()]
        .sort(([a], [b]) => b.length - a.length)
        .forEach(([english, spanish]) => { translated = translated.replaceAll(english, spanish); });
      translated = translated
        .replace("Do you want to erase the device before installing ", "¿Quieres borrar el dispositivo antes de instalar ")
        .replace("Do you want to install ", "¿Quieres instalar ");
      if (translated !== trimmed) node.nodeValue = original.replace(trimmed, translated);
      return;
    }
    if (node.shadowRoot) translateEspToolsNode(node.shadowRoot);
    node.childNodes?.forEach(visit);
  };
  root.childNodes?.forEach(visit);
};

const validatePackage = async (manifestUrl, signal) => {
  const manifestResponse = await fetch(manifestUrl, { cache: "no-store", signal });
  if (!manifestResponse.ok) throw new Error(`No se encontró el manifiesto (${manifestResponse.status}).`);
  const manifest = await manifestResponse.json();
  if (manifest.channel !== "production" || manifest.simulated !== false) throw new Error("El paquete no está identificado como firmware de producción sin simulación.");
  const builds = Array.isArray(manifest.builds) ? manifest.builds : [];
  const esp32Build = builds.find((build) => build.chipFamily === "ESP32");
  if (!esp32Build?.parts?.length) throw new Error("El manifiesto no contiene una compilación para ESP32.");

  const files = esp32Build.parts.map((part) => ({
    ...part,
    url: new URL(part.path, new URL(manifestUrl, window.location.href)).toString(),
  }));
  const checks = await Promise.all(files.map(async (file) => {
    const response = await fetch(file.url, { method: "HEAD", cache: "no-store", signal });
    return { ...file, ok: response.ok, size: Number(response.headers.get("content-length") || 0) };
  }));
  const missing = checks.filter((file) => !file.ok);
  if (missing.length) throw new Error(`Falta ${missing.map((file) => file.path).join(", ")} en el servidor.`);
  return { manifest, files: checks };
};

const FlashearUSB = () => {
  const tipoSolicitado = new URLSearchParams(window.location.search).get("tipo")?.toLowerCase();
  const [type, setType] = useState(FIRMWARES[tipoSolicitado] ? tipoSolicitado : "mixto");
  const [toolState, setToolState] = useState("loading");
  const [packageState, setPackageState] = useState({ status: "checking" });
  const [serialError, setSerialError] = useState("");
  const [eraseState, setEraseState] = useState("");
  const { can } = usePermisos();
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(new URLSearchParams(window.location.search).get('dispositivoId') || '');
  useEffect(() => { api.get('/Dispositivos/mis-dispositivos').then(r => setDevices(r.data || [])).catch(() => setDevices([])); }, []);
  useEffect(() => { if(!deviceId) return; api.get(`/Dispositivos/${deviceId}/detalles`).then(r => { const required=String(r.data.tipoFirmwareRequerido || '').toLowerCase(); if(FIRMWARES[required]) setType(required); }).catch(() => setSerialError('No se pudo consultar el firmware recomendado.')); }, [deviceId]);
  const installInProgress = useRef(false);
  const manifestUrl = useMemo(() => manifestForType(type), [type]);
  const supportsSerial = typeof navigator !== "undefined" && "serial" in navigator;
  const supportedContext = typeof window !== "undefined" && window.isSecureContext;

  useEffect(() => {
    let mounted = true;
    loadEspWebTools().then(() => mounted && setToolState("ready"))
      .catch((error) => mounted && setToolState(error.message || "error"));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const translate = () => translateEspToolsNode(document.body);
    const observer = new MutationObserver(translate);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    const interval = window.setInterval(translate, 250);
    translate();
    return () => {
      observer.disconnect();
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const message = String(event.reason?.message || event.reason || "");
      if (!/SerialPort|serial port|Failed to initialize|port is already open/i.test(message)) return;
      event.preventDefault();
      installInProgress.current = false;
      setSerialError(/already open/i.test(message)
        ? "El puerto serial ya estaba abierto. Cierra Arduino IDE y cualquier monitor serial, desconecta y vuelve a conectar el ESP32, y reintenta."
        : "No se pudo iniciar el ESP32. Cierra otros programas que usen el puerto y mantén presionado BOOT al comenzar la instalación.");
    };
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  }, []);

  const beginInstall = (event) => {
    if (installInProgress.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    installInProgress.current = true;
    setSerialError("");
    window.setTimeout(() => { installInProgress.current = false; }, 1500);
  };

  const eraseDevice = async () => {
    if(!can("FIRMWARE_TAB_FLASH_EDIT")) return;
    if (!window.confirm("Se borrará completamente el ESP32 y quedará sin firmware. ¿Continuar?")) return;
    let transport;
    setSerialError("");
    setEraseState("Selecciona el puerto USB del ESP32…");
    try {
      const port = await navigator.serial.requestPort();
      transport = new Transport(port, false);
      const terminal = { clean() {}, writeLine() {}, write() {} };
      const loader = new ESPLoader({ transport, baudrate: 115200, terminal });
      setEraseState("Conectando con el ESP32…");
      await loader.main();
      setEraseState("Formateando toda la memoria flash…");
      await loader.eraseFlash();
      setEraseState("ESP32 formateado correctamente. Ya puedes desconectarlo o instalar un firmware limpio.");
    } catch (error) {
      const message = String(error?.message || error || "");
      setEraseState("");
      setSerialError(/open|serial|port/i.test(message)
        ? "No se pudo abrir el puerto. Cierra Arduino IDE y cualquier monitor serial, vuelve a conectar el ESP32 y reintenta."
        : `No se pudo formatear el ESP32: ${message}`);
    } finally {
      if (transport) {
        try { await transport.disconnect(); } catch (_) { /* El puerto puede haberse cerrado al reiniciar. */ }
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    setPackageState({ status: "checking" });
    validatePackage(manifestUrl, controller.signal)
      .then((data) => setPackageState({ status: "ready", ...data }))
      .catch((error) => {
        if (error.name !== "AbortError") setPackageState({ status: "error", message: error.message });
      });
    return () => controller.abort();
  }, [manifestUrl]);

  const ready = supportsSerial && supportedContext && toolState === "ready" && packageState.status === "ready";

  return (
    <section className="overflow-hidden rounded-2xl border border-emerald-900/80 bg-gradient-to-br from-neutral-950 via-neutral-900 to-emerald-950/30 text-white shadow-2xl">
      <div className="border-b border-neutral-800 px-5 py-6 sm:px-7">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-400">Instalador USB · Producción sin simulación</p>
            <h3 className="text-2xl font-bold">Preparar un ESP32</h3>
            <p className="mt-2 max-w-2xl text-sm text-neutral-300">Selecciona el trabajo del dispositivo, valida el paquete y grábalo directamente desde el navegador.</p>
          </div>
          <div className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${ready ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300" : "border-amber-500/60 bg-amber-500/10 text-amber-200"}`}>
            {ready ? "Listo para conectar" : "Revisión pendiente"}
          </div>
        </div>
      </div>

      <div className="space-y-6 p-5 sm:p-7">
        <div>
          <h4 className="mb-3 text-sm font-semibold text-neutral-200">1. Elige el firmware</h4>
          <div className="grid gap-3 md:grid-cols-3">
            {Object.entries(FIRMWARES).map(([id, item]) => {
              const selected = id === type;
              return (
                <button key={id} type="button" onClick={() => setType(id)} aria-pressed={selected}
                  className={`relative min-h-36 rounded-xl border p-4 text-left transition ${selected ? "border-emerald-400 bg-emerald-500/10 ring-2 ring-emerald-500/20" : "border-neutral-700 bg-neutral-900/70 hover:border-neutral-500"}`}>
                  {item.recommended && <span className="absolute right-3 top-3 rounded-full bg-emerald-400 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-950">Recomendado</span>}
                  <span className="block text-lg font-bold">{item.name}</span>
                  <span className="mt-2 block text-xs leading-5 text-neutral-400">{item.description}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.15fr_.85fr]">
          <div className="rounded-xl border border-neutral-700 bg-neutral-950/70 p-5">
            <h4 className="text-sm font-semibold text-neutral-200">2. Verificación</h4>
            <div className="mt-4 space-y-3 text-sm">
              <StatusRow ok={supportsSerial} label="Chrome o Edge con Web Serial" detail={supportsSerial ? "Compatible" : "Abre esta página en Chrome o Edge de escritorio."} />
              <StatusRow ok={supportedContext} label="Conexión segura" detail={supportedContext ? "HTTPS activo" : "Web Serial requiere HTTPS o localhost."} />
              <StatusRow ok={toolState === "ready"} pending={toolState === "loading"} label="Herramienta de instalación" detail={toolState === "ready" ? "Cargada" : toolState === "loading" ? "Cargando…" : String(toolState)} />
              <StatusRow ok={packageState.status === "ready"} pending={packageState.status === "checking"} label={`Paquete ${FIRMWARES[type].name}`}
                detail={packageState.status === "ready" ? `${packageState.manifest.version || "sin versión"} · ${packageState.files.length} archivo(s)` : packageState.status === "checking" ? "Validando manifiesto y binarios…" : packageState.message} />
            </div>
          </div>

          <div className="rounded-xl border border-neutral-700 bg-neutral-950/70 p-5">
            <h4 className="text-sm font-semibold text-neutral-200">Antes de conectar</h4>
            <ol className="mt-3 space-y-2 text-sm leading-6 text-neutral-300">
              <li><b className="text-white">1.</b> Usa un cable USB que transmita datos.</li>
              <li><b className="text-white">2.</b> Desconecta relés, bombas y alimentación externa.</li>
              <li><b className="text-white">3.</b> Cierra Arduino IDE y cualquier monitor serial.</li>
              <li><b className="text-white">4.</b> Si no conecta, mantén presionado BOOT al iniciar.</li>
            </ol>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-700 bg-neutral-950/70 p-5">
          <h4 className="text-sm font-semibold text-neutral-200">3. Conecta y graba</h4>
          {ready ? (
            <div className="mt-4">
              <label className="block text-sm mb-3">Dispositivo (selección recomendada)<select value={deviceId} onChange={e => setDeviceId(e.target.value)} className="bg-neutral-900 rounded p-2"><option value="">Instalación inicial: elegir tipo manualmente</option>{devices.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}</select></label><p className="text-sm mb-3">Conectar USB → Instalar → Configurar red → Vincular → Verificar comunicación. El borrado elimina las credenciales locales.</p>
              {can("FIRMWARE_TAB_FLASH_EDIT") ? <esp-web-install-button key={manifestUrl} manifest={manifestUrl}>
                <button slot="activate" type="button" onClick={beginInstall} className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 px-5 py-3 font-bold text-neutral-950 shadow-lg transition hover:brightness-110 sm:w-auto">Conectar ESP32 y flashear</button>
                <span slot="unsupported" className="text-sm text-amber-300">Este navegador no admite Web Serial.</span>
                <span slot="not-allowed" className="text-sm text-amber-300">La instalación requiere HTTPS o abrir el sistema desde localhost.</span>
              </esp-web-install-button> : <p>No tienes permiso para instalar firmware.</p>}
              <details className="mt-3"><summary>Recuperación avanzada</summary><p className="text-sm">Formatear deja el ESP32 sin firmware, red ni clave IoT. Después debes reinstalarlo y vincularlo.</p><button disabled={!can("FIRMWARE_TAB_FLASH_EDIT")} type="button" onClick={eraseDevice} className="ml-0 mt-3 w-full rounded-xl border border-red-500/70 bg-red-500/10 px-5 py-3 font-bold text-red-200 transition hover:bg-red-500/20 sm:ml-3 sm:mt-0 sm:w-auto">Solo formatear ESP32</button></details>
              {eraseState && <div role="status" className="mt-4 rounded-lg border border-cyan-500/40 bg-cyan-500/10 p-4 text-sm text-cyan-100">{eraseState}</div>}
              {serialError && <div role="alert" className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{serialError}</div>}
              <p className="mt-3 text-xs text-neutral-400">Selecciona el puerto USB del ESP32 y no desconectes el cable hasta finalizar.</p>
            </div>
          ) : (
            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">El instalador se habilitará cuando el navegador y todos los archivos estén listos.</div>
          )}
        </div>
      </div>
    </section>
  );
};

const StatusRow = ({ ok, pending = false, label, detail }) => (
  <div className="flex items-start gap-3">
    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${pending ? "bg-neutral-700 text-neutral-300" : ok ? "bg-emerald-500 text-emerald-950" : "bg-red-500/20 text-red-300"}`}>{pending ? "…" : ok ? "✓" : "!"}</span>
    <div><div className="font-medium text-neutral-200">{label}</div><div className={`text-xs ${ok ? "text-neutral-400" : "text-amber-200"}`}>{detail}</div></div>
  </div>
);

export default FlashearUSB;
